import React, { useCallback, useEffect, useState } from 'react'

import { Alert, Button, Card, EmptyState, Grid, Header, Row, Table, Text } from '@plurall/elo-beta'

import { isMobile, responsibleStudentsTableDataFormat, responsibleStudentsTableHeader } from 'utils'
import { useResponsibleLinkStudentModal } from 'hooks'
import { graphqlClient } from 'utils/graphqlClient'
import LoadingContainer from 'App/components/LoadingContainer/LoadingContainer'
import MainMenu from 'App/components/MainMenu/MainMenu'
import MobileButton from 'App/components/MobileButton'
import { GET_PERSON_RESPONSIBLE_DEPENDENTS } from 'graphql/queries'
import { REMOVE_RELATED_DEPENDENT } from 'graphql/mutations'

import styles from './DependentsData.module.css'

const DependentsData = () => {
  const [dependents, setDependents] = useState([])
  const [loading, setLoading] = useState(null)
  const [isRemovingRelatedStudent, setIsRemovingRelatedStudent] = useState(false)
  const {
    setIsResponsibleLinkStudentModalOpen,
    hasToRefresh,
    setHasToRefresh,
  } = useResponsibleLinkStudentModal()
  const [isRemoveRelatedStudentAlertOpen, setIsRemoveRelatedStudentAlertOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState({
    id: null,
    name: '',
  })
  const [parentId, setParentId] = useState(null)

  const resetSelectedStudent = useCallback(() => {
    setSelectedStudent({
      id: null,
      name: '',
    })
  }, [])

  const openRemoveRelatedStudentAlert = useCallback((studentId, studentName) => {
    setIsRemoveRelatedStudentAlertOpen(true)
    setSelectedStudent({
      id: studentId,
      name: studentName,
    })
  }, [])

  const closeRemoveRelatedStudentAlert = useCallback(() => {
    setIsRemoveRelatedStudentAlertOpen(false)
    resetSelectedStudent()
  }, [resetSelectedStudent])

  const getDependents = useCallback(async () => {
    try {
      setLoading(true)
      const {
        data: {
          person: { dependents: data, id },
        },
      } = await graphqlClient().query({
        query: GET_PERSON_RESPONSIBLE_DEPENDENTS,
      })

      setParentId(id)
      setDependents(responsibleStudentsTableDataFormat(data, openRemoveRelatedStudentAlert))
    } catch (error) {
      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'negative',
        text: 'Erro ao carregar dados dos dependentes',
      })
    } finally {
      setLoading(false)
    }
  }, [openRemoveRelatedStudentAlert])

  const removeRelatedStudent = useCallback(async () => {
    try {
      setIsRemovingRelatedStudent(true)
      await graphqlClient().mutate({
        mutation: REMOVE_RELATED_DEPENDENT,
        variables: {
          parentId: +parentId,
          studentId: +selectedStudent.id,
        },
      })

      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'positive',
        text: 'Aluno desvinculado com sucesso',
      })

      await getDependents()
    } catch (error) {
      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'negative',
        text: 'Erro ao desvincular aluno',
      })
    } finally {
      setIsRemovingRelatedStudent(false)
      closeRemoveRelatedStudentAlert()
    }
  }, [closeRemoveRelatedStudentAlert, getDependents, parentId, selectedStudent.id])

  useEffect(() => {
    getDependents()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasToRefresh) {
      getDependents()
      setHasToRefresh(false)
    }
  }, [getDependents, hasToRefresh, setHasToRefresh])

  if (loading) {
    return <LoadingContainer text='Carregando informações...' fullscreen />
  }

  return (
    <>
      <Header description='Gerencie os alunos que estão vinculados a sua conta'>
        Alunos vinculados
      </Header>
      <Grid desktop={12}>
        <Row>
          <MainMenu />
          <Grid desktop={9}>
            <Card
              title='Alunos vinculados'
              description='Alunos que estão vinculados à sua conta'
              renderHeaderActions={() =>
                dependents.length > 0 &&
                (isMobile() ? (
                  <MobileButton onClick={() => setIsResponsibleLinkStudentModalOpen(true)} />
                ) : (
                  <Button secondary onClick={() => setIsResponsibleLinkStudentModalOpen(true)}>
                    Vincular aluno
                  </Button>
                ))
              }
            >
              <div className={styles['table-list']}>
                {dependents.length ? (
                  <Table
                    headers={responsibleStudentsTableHeader}
                    data={dependents}
                    dataTestId={'card-dependent-data-table'}
                    ellipsis
                  />
                ) : (
                  <EmptyState
                    button={
                      <Button
                        dataTestId='card-dependent-empty-state-action-button'
                        secondary
                        onClick={() => setIsResponsibleLinkStudentModalOpen(true)}
                        className={styles['empty-state-action-button']}
                      >
                        Vincular aluno
                      </Button>
                    }
                    icon='Link'
                    title='Nenhum aluno vinculado à sua conta'
                  >
                    <Text
                      size='t3'
                      alignment='center'
                      dimmed
                      dataTestId='card-dependent-empty-state-action-text'
                    >
                      Para vincular sua conta a um aluno, clique em "Vincular aluno".
                    </Text>
                  </EmptyState>
                )}
              </div>
            </Card>
          </Grid>
        </Row>
      </Grid>

      {isRemoveRelatedStudentAlertOpen && (
        <Alert
          title={'Desvincular aluno'}
          secondaryButtonText='Cancelar'
          primaryButtonText='Desvincular'
          onSecondaryClick={closeRemoveRelatedStudentAlert}
          onPrimaryClick={removeRelatedStudent}
          dataTestId='dependent-remove-related-student-alert'
          primaryButtonLoading={isRemovingRelatedStudent}
          closePrimaryClick={false}
          secondaryButtonDisabled={isRemovingRelatedStudent}
          variant='attention'
        >
          Você deseja desvincular {selectedStudent.name} da sua conta?
        </Alert>
      )}
    </>
  )
}

export default DependentsData
